import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ReceivedCode } from '../../types/api';
import { formatDate } from '../../utils/dateFormatter';
import { useLanguage } from '../../hooks/useLanguage';

const DEFAULT_PAGE_SIZE = 5;

interface ReceivedCodesTableProps {
  receivedCodes: ReceivedCode[];
  pageSize?: number;
  className?: string;
}

export function ReceivedCodesTable({
  receivedCodes,
  pageSize = DEFAULT_PAGE_SIZE,
  className = '',
}: ReceivedCodesTableProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [page, setPage] = useState(1);

  const sorted = [...receivedCodes].sort(
    (a, b) => new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime()
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageItems = sorted.slice(start, start + pageSize);

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.copyError') || t('errors.network'));
    }
  };

  if (sorted.length === 0) {
    return (
      <p className="text-neutral-medium font-body text-sm py-4">
        {t('temporaryNumbers.sms.noCodes')}
      </p>
    );
  }

  return (
    <div className={className}>
      <h3 className="text-base sm:text-lg font-heading font-semibold text-neutral-light mb-3 sm:mb-4">
        {t('temporaryNumbers.sms.receivedCodes')}
      </h3>

      {/* Desktop: table */}
      <div className="hidden sm:block overflow-x-auto rounded-xl border border-neutral-dark">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-neutral-dark bg-neutral-dark/30">
              <th className="px-4 py-3 font-heading font-semibold text-neutral-light">
                {t('temporaryNumbers.sms.code')}
              </th>
              <th className="px-4 py-3 font-heading font-semibold text-neutral-light">
                {t('temporaryNumbers.sms.receivedAt')}
              </th>
              <th className="w-12 px-2 py-3" aria-label={t('common.copy')} />
            </tr>
          </thead>
          <tbody>
            {pageItems.map((item, idx) => (
              <tr
                key={`${item.code}-${item.receivedAt}-${idx}`}
                className="border-b border-neutral-dark/50 hover:bg-neutral-dark/20"
              >
                <td className="px-4 py-3 font-mono font-semibold text-brand-accent">
                  {item.code}
                </td>
                <td className="px-4 py-3 text-neutral-light font-body">
                  {formatDate(item.receivedAt, 'PP h:mm a', currentLanguage)}
                </td>
                <td className="px-2 py-3">
                  <button
                    type="button"
                    onClick={() => handleCopy(item.code)}
                    className="p-2 rounded-lg hover:bg-brand-accent/20 text-brand-accent transition-colors"
                    title={t('common.copy')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: cards */}
      <div className="sm:hidden space-y-2">
        {pageItems.map((item, idx) => (
          <div
            key={`${item.code}-${item.receivedAt}-${idx}`}
            className="flex items-center justify-between gap-3 rounded-xl border border-neutral-dark bg-neutral-dark/30 p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="font-mono font-semibold text-brand-accent truncate">{item.code}</p>
              <p className="text-xs text-neutral-medium font-body mt-0.5">
                {formatDate(item.receivedAt, 'PP h:mm a', currentLanguage)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(item.code)}
              className="shrink-0 p-2 rounded-lg bg-brand-accent/20 text-brand-accent"
              title={t('common.copy')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-neutral-dark">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="px-3 py-2 text-sm font-body text-neutral-light bg-neutral-dark/50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-dark/70"
          >
            {t('common.previous')}
          </button>
          <span className="text-sm text-neutral-medium font-body">
            {t('temporaryNumbers.pagination.page', { current: currentPage, total: totalPages })}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="px-3 py-2 text-sm font-body text-neutral-light bg-neutral-dark/50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-neutral-dark/70"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
}
